import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useMeuPlayState } from "@src/states/meuplay";
import { usePlayerState } from "@src/states/player";

import { player } from "@src/services/player";

import { tryExitOfflineMode } from "@src/utils/offline/tryExitOfflineMode";

export function useTabFocusRecovery(queryClient: QueryClient) {
  useEffect(() => {
    function onVisibilityChange() {
      const { currentTrackId } = usePlayerState.getState();

      if (document.visibilityState === "hidden") {
        // Persiste o estado do player antes de perder foco
        useMeuPlayState.setState({
          playingOrPausedOnBlur: player.getPlayingOrPaused(currentTrackId),
        });
        return;
      }

      // Aba voltou ao foco — consome e limpa o valor persistido
      const { playingOrPausedOnBlur } = useMeuPlayState.getState();
      useMeuPlayState.setState({ playingOrPausedOnBlur: null });

      const runtimePlayingOrPaused = player.getPlayingOrPaused(currentTrackId);

      if (playingOrPausedOnBlur !== "playing") {
        console.log("[useTabFocusRecovery] Não estava tocando ao perder foco, nada a fazer");
        return;
      }
      if (runtimePlayingOrPaused === "playing") {
        console.log("[useTabFocusRecovery] Ainda está tocando, nada a fazer");
        return;
      }

      // Estava tocando antes de perder foco mas não está mais tocando agora
      const isOffline = currentTrackId === "offline";
      if (!isOffline) {
        console.log("[useTabFocusRecovery] Não está no modo offline, nada a fazer");
        return;
      }

      tryExitOfflineMode(queryClient, "playing").then((exited) => {
        if (exited) {
          console.log("[useTabFocusRecovery] Retomando stream online após retorno de foco");
        } else {
          console.log("[useTabFocusRecovery] Sem internet ao retomar foco, permanecendo offline");
        }
      });
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [queryClient]);
}
